/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.dev/license
 */
import {FlatTreeControl} from '@angular/cdk/tree';
import {ChangeDetectionStrategy, Component, EventEmitter, Injectable, Input, Output, inject} from '@angular/core';
import {MatTreeFlatDataSource, MatTreeFlattener, MatTreeModule} from '@angular/material/tree';
import {BehaviorSubject, Observable} from 'rxjs';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {ENTER, SPACE} from '@angular/cdk/keycodes';
import { getAllCategories } from '@dataconnect/account';
import { CategoryPublic } from '../../../../../globalTypes/category.types';

let loadMoreId = 1;

export interface CategorySelected {
  name: string;
  id: number;
  isDigital: boolean;
}

/** Nested node */
class NestedNode {
  childrenChange = new BehaviorSubject<NestedNode[]>([]);

  get children(): NestedNode[] {
    return this.childrenChange.value;
  }

  constructor(
    public name: string,
    public hasChildren = false,
    public parent: string | null = null,
    public isLoadMore = false,
  ) {}
}

/** Flat node with expandable and level information */
export class FlatNode {
  constructor(
    public name: string,
    public level = 1,
    public expandable = false,
    public parent: string | null = null,
    public isLoadMore = false,
  ) {}
}

/** Number of nodes loaded at a time */
const batchSize = 3;

/**
 * A database that only load part of the data initially. After user clicks on the `Load more`
 * button, more data will be loaded.
 */
@Injectable()
export class LoadmoreDatabase {
  /** Map of node name to node */
  nodes = new Map<string, NestedNode>();

  dataChange = new BehaviorSubject<NestedNode[]>([]);

  categoryIdByName: {[name: string]: number} = {}
  isDigitalByName: {[name: string]: boolean} = {}

  /** Example data */
  rootNodes: string[] = ['Vegetables', 'Fruits'];
  childMap = new Map<string, string[]>([
    ['Fruits', ['Apple', 'Orange', 'Banana']],
    ['Vegetables', ['Tomato', 'Potato', 'Onion']],
    [
      'Apple',
      [
        'Gala',
        'Braeburn',
        'Fuji',
        'Macintosh',
        'Golden Delicious',
        'Red Delicious',
        'Empire',
        'Granny Smith',
        'Cameo',
        'Baldwin',
        'Jonagold',
      ],
    ],
    ['Onion', ['Yellow', 'White', 'Purple', 'Green', 'Shallot', 'Sweet', 'Red', 'Leek']],
  ]);

  async initialize() {
    const dataRef = await getAllCategories();
    const categories = dataRef.data.categories as unknown as CategoryPublic[];
    this.rootNodes = categories.filter(cat=>!cat.parentId).map(cat=>cat.name);
    this.childMap = this.buildParentChildMap(categories);
    console.log({childMap: this.childMap})
    const data = this.rootNodes.map(name => this._generateNode(name, null));
    this.dataChange.next(data);
  }

  buildParentChildMap(categories: CategoryPublic[]): Map<string, string[]> {
    const map = new Map<string, string[]>();

    // index categories by ID for quick lookup
    const byId = new Map<number, CategoryPublic>();
    categories.forEach(cat => {
      byId.set(cat.id, cat)
      this.categoryIdByName[cat.name] = cat.id;
      this.isDigitalByName[cat.name] = cat.isDigital || false;
    });

    categories.forEach(cat => {
      if (cat.parentId) {
        const parent = byId.get(cat.parentId);
        if (parent) {
          if (!map.has(parent.name)) {
            map.set(parent.name, []);
          }
          map.get(parent.name)!.push(cat.name);
        }
      }
    });

    return map;
  }

  /** Expand a node whose children are not loaded */
  loadChildren(name: string, onlyFirstTime = false) {
    if (!this.nodes.has(name) || !this.childMap.has(name)) {
      return;
    }
    const parent = this.nodes.get(name)!;
    const children = this.childMap.get(name)!;

    if (onlyFirstTime && parent.children!.length > 0) {
      return;
    }

    const newChildrenNumber = parent.children!.length + batchSize;
    const nodes = children
      .slice(0, newChildrenNumber)
      .map(name => this._generateNode(name, parent.name));
    if (newChildrenNumber < children.length) {
      // Need a new "Load More" node
      nodes.push(new NestedNode(`LOAD_MORE-${loadMoreId++}`, false, name, true));
    }

    parent.childrenChange.next(nodes);
    this.dataChange.next(this.dataChange.value);
  }

  private _generateNode(name: string, parent: string | null): NestedNode {
    if (!this.nodes.has(name)) {
      this.nodes.set(name, new NestedNode(name, this.childMap.has(name), parent));
    }

    return this.nodes.get(name)!;
  }
}

/**
 * @title Tree with partially loaded data
 */
@Component({
  selector: 'msh-category-selector',
  template: `
  <mat-tree style="background: transparent;" [dataSource]="dataSource" [treeControl]="treeControl">
  <!-- Leaf node -->
  <mat-tree-node *matTreeNodeDef="let node" matTreeNodePadding  [matTreeNodePaddingIndent]="padding"
      (click)="select($event, node)"
    class="flex !items-start">
    <button matIconButton
      [attr.aria-label]="'Toggle ' + node.name"><mat-icon [class]="'text-sm '+(selectedCategoryNames.includes(node.name) ? 'text-orange-400' : ' opacity-50')">check_circle</mat-icon></button>
    {{node.name}}
  </mat-tree-node>

  <!-- expandable node -->
  <mat-tree-node *matTreeNodeDef="let node; when: hasChild" matTreeNodePadding  [matTreeNodePaddingIndent]="padding" matTreeNodeToggle
    (expandedChange)="loadChildren(node)" [cdkTreeNodeTypeaheadLabel]="node.name"
    class="flex !items-start font-light">
    <button matIconButton
      [attr.aria-label]="'Toggle ' + node.name"
      matTreeNodeToggle>
      <mat-icon class="mat-icon-rtl-mirror">
        {{treeControl.isExpanded(node) ? 'expand_more' : 'chevron_right'}}
      </mat-icon>
    </button>
    {{node.name}}
  </mat-tree-node>

  <mat-tree-node class=" round px-4 cursor-pointer hover:bg-navy/15 focus:bg-navy-50 example-load-more" *matTreeNodeDef="let node; when: isLoadMore"
    role="treeitem" (click)="loadOnClick($event, node)"
    (keydown)="loadOnKeypress($event, node)">
    Load more of {{node.parent}}...
  </mat-tree-node>
</mat-tree>

`,
  styles: [`
   
    `],
  providers: [LoadmoreDatabase],
  imports: [MatTreeModule, MatButtonModule, MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CategorySelectorComponent {
  private _database = inject(LoadmoreDatabase);
  padding = 20
  nodeMap = new Map<string, FlatNode>();
  treeControl: FlatTreeControl<FlatNode>;
  treeFlattener: MatTreeFlattener<NestedNode, FlatNode>;
  // Flat tree data source
  dataSource: MatTreeFlatDataSource<NestedNode, FlatNode>;
  @Input() mode: 'single' | 'multiple' = 'multiple';
  @Input() selectedCategoryNames: string[] = [];
  @Output() onSelectCategory: EventEmitter<CategorySelected[]> = new EventEmitter;

  constructor() {
    const _database = this._database;

    this.treeFlattener = new MatTreeFlattener(
      this.transformer,
      this.getLevel,
      this.isExpandable,
      this.getChildren,
    );

    // TODO(#27626): Remove treeControl. Adopt either levelAccessor or childrenAccessor.
    this.treeControl = new FlatTreeControl<FlatNode>(this.getLevel, this.isExpandable);

    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

    _database.dataChange.subscribe(data => {
      this.dataSource.data = data;
    });

    _database.initialize();
  }

  getChildren = (node: NestedNode): Observable<NestedNode[]> => node.childrenChange;

  transformer = (node: NestedNode, level: number) => {
    const existingNode = this.nodeMap.get(node.name);

    if (existingNode) {
      return existingNode;
    }

    const newNode = new FlatNode(node.name, level, node.hasChildren, node.parent, node.isLoadMore);
    this.nodeMap.set(node.name, newNode);
    return newNode;
  };

  getLevel = (node: FlatNode) => node.level;

  isExpandable = (node: FlatNode) => node.expandable;

  hasChild = (_: number, node: FlatNode) => node.expandable;

  isLoadMore = (_: number, node: FlatNode) => node.isLoadMore;

  loadChildren(node: FlatNode) {
    this._database.loadChildren(node.name, true);
  }

  /** Load more nodes when clicking on "Load more" node. */
  loadOnClick(event: MouseEvent, node: FlatNode) {
    this._loadSiblings(event.target as HTMLElement, node);
  }

  /** Load more nodes on keyboardpress when focused on "Load more" node */
  loadOnKeypress(event: KeyboardEvent, node: FlatNode) {
    if (event.keyCode === ENTER || event.keyCode === SPACE) {
      this._loadSiblings(event.target as HTMLElement, node);
    }
  }

  private _loadSiblings(nodeElement: HTMLElement, node: FlatNode) {
    if (node.parent) {
      // Store a reference to the sibling of the "Load More" node before it is removed from the DOM
      const previousSibling = nodeElement.previousElementSibling;

      // Synchronously load data.
      this._database.loadChildren(node.parent);

      const focusDesination = previousSibling?.nextElementSibling || previousSibling;

      if (focusDesination) {
        // Restore focus.
        (focusDesination as HTMLElement).focus();
      }
    }
  }

  select(ev:any, node: FlatNode){
    console.log({selectedCategoryNames: this.selectedCategoryNames, node})
    if(this.selectedCategoryNames.includes(node.name)){
      this.selectedCategoryNames = this.selectedCategoryNames.filter(cat=>cat!==node.name);
    } else if(this.mode === 'single'){
      this.selectedCategoryNames = [node.name];
    } else {
      this.selectedCategoryNames.push(node.name);
    }
  
    this.onSelectCategory.emit(this.selectedCategoryNames.map(cat=>({name: cat, isDigital:this._database.isDigitalByName[cat], id:this._database.categoryIdByName[cat]})));
  }
}
